import { useEffect, useMemo, useState } from 'react';
import { addDays, format, startOfWeek, parse } from 'date-fns';

import {
	useFullFlightsScheduleLazyQuery,
	FlightsScheduleParameters,
	SegmentSearchParameter,
	useFlightsScheduleLazyQuery
} from '@websky/graphql';

import { API_DATE_FORMAT } from '../utils';
import { resolveScheduleReferences, ResolvedSchedule } from './lib/resolveScheduleReferences';

export const useSchedule = (): [
	(parameters: FlightsScheduleParameters, startDate?: Date) => void,
	{
		schedule: ResolvedSchedule;
		defaultDate: string;
		availableDates: Date[];
		isLoading: boolean;
	}
] => {
	const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

	const [getFlightsSchedule, { data, loading }] = useFullFlightsScheduleLazyQuery({ fetchPolicy: 'no-cache' });

	const schedule = useMemo(
		() => (data?.FullFlightsSchedule ? resolveScheduleReferences(data.FullFlightsSchedule) : null),
		[data?.FullFlightsSchedule]
	);

	const getSchedule = async (parameters: FlightsScheduleParameters, date: Date = startDate) => {
		await getFlightsSchedule({
			variables: {
				parameters: [parameters],
				period: {
					startDate: format(date, API_DATE_FORMAT),
					endDate: format(addDays(date, 6), API_DATE_FORMAT)
				},
				groupEqualFlights: true
			}
		});

		setStartDate(date);
	};

	const segmentParameters: SegmentSearchParameter = useMemo(() => {
		const segment = schedule?.searchParameters.segments?.[0];

		if (segment) {
			return {
				departure: {
					iata: segment.departure.iata
				},
				arrival: {
					iata: segment.arrival.iata
				},
				date: format(startDate, API_DATE_FORMAT)
			};
		}

		return null;
	}, [schedule, startDate]);

	const { dates, isLoading } = useAvailableDates(segmentParameters);

	const defaultDate = useMemo(() => format(startDate, API_DATE_FORMAT), [startDate]);

	return [
		getSchedule,
		{
			schedule,
			defaultDate,
			availableDates: dates,
			isLoading: loading || isLoading
		}
	];
};

export const useAvailableDates = (segment?: SegmentSearchParameter) => {
	const [getFlightsSchedule, { data: availableDates, loading, error, refetch }] = useFlightsScheduleLazyQuery();

	useEffect(() => {
		if (segment) {
			getFlightsSchedule({
				variables: {
					parameters: {
						departure: segment!.departure.iata,
						arrival: segment!.arrival.iata
					}
				}
			});
		}
	}, [segment, getFlightsSchedule]);

	const dates = useMemo(() => {
		if (availableDates?.FlightsSchedule.datesData) {
			return availableDates?.FlightsSchedule.datesData
				.filter(date => date?.date)
				.map(date => parse(date.date, API_DATE_FORMAT, new Date()));
		}

		return [];
	}, [availableDates?.FlightsSchedule]);

	return {
		dates,
		isLoading: loading,
		error,
		refetch
	};
};
